import axios from 'axios';
import { Employe, EmployeForm } from '../types';
import { tokenManager } from '@shared/infrastructure/security';

const BASE = '/api/v1/paie/employes';

export interface ResultatImportExcel {
  nomProjet: string;
  idProjet: string;
  crees: number;
  ignores: string[];
  erreurs: Array<{ ligne: number; message: string }>;
}

export const employesApi = {
  lister: (params?: { actif?: boolean; recherche?: string; page?: number; limite?: number; idProjet?: string }) =>
    axios.get(BASE, { params }).then(r => {
      const d = r.data;
      if (d?.succes) {
        const payload = d.data ?? d.donnees;
        if (payload?.items !== undefined) return payload as { items: Employe[]; total: number };
        return { items: (Array.isArray(payload) ? payload : []) as Employe[], total: Array.isArray(payload) ? payload.length : 0 };
      }
      const items = (Array.isArray(d) ? d : []) as Employe[];
      return { items, total: items.length };
    }),

  obtenirParId: (id: string) =>
    axios.get(`${BASE}/${id}`).then(r => {
      const d = r.data;
      return (d?.succes ? (d.data ?? d.donnees) : d) as Employe;
    }),

  creer: (data: EmployeForm) =>
    axios.post(BASE, data).then(r => {
      const d = r.data;
      return (d?.succes ? (d.data ?? d.donnees) : d) as Employe;
    }),

  modifier: (id: string, data: Partial<EmployeForm>) =>
    axios.put(`${BASE}/${id}`, data).then(r => {
      const d = r.data;
      return (d?.succes ? (d.data ?? d.donnees) : d) as Employe;
    }),

  desactiver: (id: string) =>
    axios.delete(`${BASE}/${id}`).then(r => r.data?.data ?? r.data),

  supprimerDefinitivement: (id: string) =>
    axios.delete(`${BASE}/${id}/definitif`).then(r => r.data?.data ?? r.data),

  reactiver: (id: string) =>
    axios.put(`${BASE}/${id}`, { actif: true }).then(r => r.data?.data ?? r.data),

  importerCsv: (fichier: File) => {
    const form = new FormData();
    form.append('fichier', fichier);
    return axios.post(`${BASE}/import`, form).then(r => r.data?.data ?? r.data);
  },

  listerPrimes: (idEmploye: string) =>
    axios.get(`${BASE}/${idEmploye}/primes`).then(r => r.data?.data ?? r.data),

  attribuerPrime: (idEmploye: string, data: any) =>
    axios.post(`${BASE}/${idEmploye}/primes`, data).then(r => r.data?.data ?? r.data),

  modifierAttribution: (idEmploye: string, idAttribution: string, data: any) =>
    axios.put(`${BASE}/${idEmploye}/primes/${idAttribution}`, data).then(r => r.data?.data ?? r.data),

  suspendreAttribution: (idEmploye: string, idAttribution: string) =>
    axios.delete(`${BASE}/${idEmploye}/primes/${idAttribution}/suspendre`).then(r => r.data?.data ?? r.data),

  listerOppositions: (idEmploye: string) =>
    axios.get(`${BASE}/${idEmploye}/oppositions`).then(r => r.data?.data ?? r.data),

  creerOpposition: (idEmploye: string, data: {
    type: string; libelle: string; montant: number;
    reference: string; dateDebut: string; dateFin?: string;
  }) => axios.post(`${BASE}/${idEmploye}/oppositions`, data).then(r => r.data?.data ?? r.data),

  modifierOpposition: (idEmploye: string, idOpposition: string, data: any) =>
    axios.put(`${BASE}/${idEmploye}/oppositions/${idOpposition}`, data).then(r => r.data?.data ?? r.data),

  desactiverOpposition: (idEmploye: string, idOpposition: string) =>
    axios.delete(`${BASE}/${idEmploye}/oppositions/${idOpposition}`).then(r => r.data?.data ?? r.data),

  telechargerTemplateExcel: async () => {
    const headers: Record<string, string> = {};
    const authHeader = tokenManager.getAuthorizationHeader();
    if (authHeader) headers['Authorization'] = authHeader;

    const response = await fetch(`${BASE}/template-excel`, { headers });
    if (!response.ok) throw new Error(`Erreur ${response.status} lors du téléchargement du modèle`);

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'TEMPLATE_IMPORT_EMPLOYES.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  lierCompte: (idEmploye: string, idUtilisateur: string) =>
    axios.post(`${BASE}/${idEmploye}/lier-compte`, { idUtilisateur }).then(r => r.data?.data ?? r.data),

  importerExcel: (fichier: File): Promise<ResultatImportExcel> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target!.result as ArrayBuffer;
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          bytes.forEach(b => { binary += String.fromCharCode(b); });
          const fichierBase64 = btoa(binary);
          const res = await axios.post<{ succes: boolean; donnees: ResultatImportExcel }>(
            `${BASE}/importer-excel`,
            { fichierBase64 },
          );
          resolve((res.data as any).data ?? (res.data as any).donnees ?? (res.data as any));
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(fichier);
    }),
};
